import React, { PureComponent } from 'react';
import { some, omit, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { TextField } from '@src/components/TextField';
import { Button } from '@src/components/Button';
import Checkbox from '@src/components/Checkbox';
import { DropdownInput } from '@src/components/Select/DropdownInput';
import apolloClient from '@src/graphql/apollo';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackAddInvoiceDetails, trackEvent } from '@src/metrics';
import countries from '@src/lib/countries.json';
import { InvoicingDetailsDocument, UpdateInvoicingDetailsDocument } from '@src/graphql/generated';
import './styles.scss';

const OPTIONAL_FIELDS = ['vatId', 'region', 'billingEmail'];

export default class InvoiceDetails extends PureComponent {
  static propTypes = {
    buttonLabel: PropTypes.string,
    checkTerms: PropTypes.bool,
    onUpdated: PropTypes.func,
  };

  state = {
    customerId: null,
    details: {},
    errors: {},
    sending: false,
    tosAccepted: false,
  };

  fetchDetails = async () => {
    const {
      data: { company, customer },
    } = await apolloClient.query({ query: InvoicingDetailsDocument });
    this.setInitialDetails(company, customer);
  };

  setInitialDetails = (company, customer) => {
    let { invoicingDetails: initialDetails } = company;

    initialDetails = initialDetails || {
      addressLine1: null,
      businessName: null,
      city: null,
      country: null,
      countryName: null,
      postalCode: null,
      region: null,
      vatId: null,
    };

    const invoicingDetails = {
      ...initialDetails,
      country: initialDetails.countryName ? countries.find(item => initialDetails.countryName === item.name) : null,
    };

    this.setState({
      companyId: company.id,
      customerId: customer.id,
      details: omit(invoicingDetails, ['id', 'countryName', '__typename']),
      errors: {},
      fetched: true,
      synced: true,
    });
  };

  handleSave = async () => {
    const { customerId } = this.state;
    const { onUpdated } = this.props;

    const details = { ...this.state.details };
    const errors = { ...this.state.errors };

    for (const key of Object.keys(details)) {
      if (typeof details[key] === 'string') {
        details[key] = details[key].trim();
      }

      if (!OPTIONAL_FIELDS.includes(key) && isEmpty(details[key])) {
        errors[key] = true;
      }
    }

    if (some(Object.values(errors))) {
      this.setState({ errors });
    } else if (details) {
      this.setState({ sending: true });

      try {
        await apolloClient.mutate({
          mutation: UpdateInvoicingDetailsDocument,
          variables: {
            customerId: customerId,
            invoicingDetailsInput: details,
          },
        });

        this.setState({ sending: false });
        onUpdated && onUpdated();
      } catch (e) {
        this.setState({ sending: false });

        if (e.body && e.body.key === 'validation_failed') {
          this.setState({ errors: e.body.errors });
        }
      }
    }

    trackEvent({
      context: MetricContext.ADD_PAYMENT_METHOD,
      interaction: MetricAction.ADD_NEW_PAYMENT_METHOD,
      payload: { Type: 'Invoice' },
    });
  };

  handleChange(key, value) {
    this.setState(({ details, errors }) => ({
      details: {
        ...details,
        [key]: value,
      },
      errors: key === 'country' ? { ...errors, country: false } : errors,
      synced: false,
    }));
  }

  validate(key) {
    const valid = !!this.state.details && !!this.state.details[key];
    this.setState(({ errors }) => ({
      errors: { ...errors, [key]: !valid },
    }));

    return valid;
  }

  componentDidMount() {
    this.fetchDetails();
  }

  render() {
    const { buttonLabel, checkTerms } = this.props;
    const { fetched, errors, tosAccepted, synced, sending } = this.state;
    const details = this.state.details || {};

    if (!fetched) {
      return null;
    }

    const termsRequired = checkTerms && !tosAccepted;

    return (
      <div className="invoicing-details-form">
        <div
          className={classnames('invoicing-details-form__container', {
            'invoicing-details-form__container--saving': sending,
          })}
        >
          <div className="invoicing-details-form__section">
            <DropdownInput
              value={details.country ? { label: details.country.name, value: details.country.iso } : undefined}
              isInvalid={!!errors.country}
              onChange={option => {
                const selectedCountry = countries.find(item => item.iso === option?.value);

                if (selectedCountry) {
                  this.handleChange('country', selectedCountry);

                  trackAddInvoiceDetails('Country');
                }
              }}
              options={countries.map(item => {
                return {
                  label: item.name,
                  value: item.iso,
                };
              })}
              placeholder="Country"
            />
          </div>

          <div className="invoicing-details-form__section">
            <TextField
              defaultValue={details.businessName}
              label="Business Name"
              messageType={!!errors.businessName && 'error'}
              onBlur={() => {
                this.validate('businessName');
                trackAddInvoiceDetails('Business Name');
              }}
              onChange={value => this.handleChange('businessName', value)}
              wrapperClassName="invoicing-details-form__input"
            />

            <TextField
              defaultValue={details.vatId}
              label="VAT ID"
              onBlur={() => trackAddInvoiceDetails('VAT ID')}
              onChange={value => this.handleChange('vatId', value)}
              wrapperClassName="invoicing-details-form__input"
            />
          </div>

          <div className="invoicing-details-form__section">
            <TextField
              defaultValue={details.addressLine1}
              label="Address line 1"
              messageType={!!errors.addressLine1 && 'error'}
              onBlur={() => {
                this.validate('addressLine1');
                trackAddInvoiceDetails('Address line 1');
              }}
              onChange={value => this.handleChange('addressLine1', value)}
              wrapperClassName="invoicing-details-form__input"
            />

            <TextField
              defaultValue={details.city}
              label="City"
              messageType={!!errors.city && 'error'}
              onBlur={() => {
                this.validate('city');
                trackAddInvoiceDetails('City');
              }}
              onChange={value => this.handleChange('city', value)}
              wrapperClassName="invoicing-details-form__input"
            />
          </div>

          <div className="invoicing-details-form__section">
            <TextField
              defaultValue={details.region}
              label="State/Province/Region"
              onBlur={() => trackAddInvoiceDetails('State/Province/Region')}
              onChange={value => this.handleChange('region', value)}
              wrapperClassName="invoicing-details-form__input"
            />

            <TextField
              defaultValue={details.postalCode}
              label="Postal Code"
              messageType={!!errors.postalCode && 'error'}
              onBlur={() => {
                this.validate('postalCode');
                trackAddInvoiceDetails('Postal Code');
              }}
              onChange={value => this.handleChange('postalCode', value)}
              wrapperClassName="invoicing-details-form__input"
            />
          </div>

          <div className="invoicing-details-form__section">
            <TextField
              defaultValue={details.billingEmail}
              label="Billing e-mail"
              onBlur={() => trackAddInvoiceDetails('Billing e-mail')}
              onChange={value => this.handleChange('billingEmail', value)}
              wrapperClassName="invoicing-details-form__input"
            />
          </div>

          {checkTerms && (
            <Checkbox
              label={
                <span>
                  I accept the
                  <a href="/terms" target="_">
                    Terms &amp; Conditions
                  </a>
                </span>
              }
              onChange={() =>
                this.setState(
                  ({ tosAccepted }) => ({
                    tosAccepted: !tosAccepted,
                  }),
                  () => trackAddInvoiceDetails('Terms and Conditions'),
                )
              }
              value={tosAccepted}
              wrapperClassName="tos-checkbox"
            />
          )}
        </div>

        <div className="invoicing-details-form__publish">
          <Button isDisabled={sending || termsRequired || (!checkTerms && synced)} onClick={this.handleSave}>
            {buttonLabel || (synced ? 'Save' : 'Update')}
          </Button>
        </div>
      </div>
    );
  }
}
